import React, { useState } from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import { getCiReportHighValueDates } from "../../../api/competitiveIntelligence";
import { InferredOccupancy } from "../../../utils/ciFormat";
import ThinScroll from "../../shared/ThinScroll";
import SelectMenu from "../../shared/SelectMenu";
import { PanelTakeaway, fmtDate, fmtRate, fmtPct } from "../shared";

// CAPTURE NOW = urgent emerald (raise prices NOW — competitors selling out)
// RAISE PRICE = blue (less urgent)
// MONITOR     = amber
// HOLD        = gray
const ACTION_BADGE = {
  "CAPTURE NOW": "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  "RAISE PRICE": "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  "MONITOR": "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  "HOLD": "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
};

const ROW_TINT = {
  "CAPTURE NOW": "bg-emerald-50/70 dark:bg-emerald-900/15 hover:bg-emerald-50 dark:hover:bg-emerald-900/25",
};

const ActionBadge = ({ action }) => {
  const cls = ACTION_BADGE[action] || ACTION_BADGE.HOLD;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] uppercase tracking-wide font-semibold ${cls}`}>
      {action || "—"}
    </span>
  );
};

const HighValueDatesBody = ({ rows, limit, onLimitChange }) => (
  <div>
    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center gap-3 flex-wrap">
      <span className="inline-flex items-center gap-1.5">
        <span className="text-sm text-gray-600 dark:text-gray-300">Top:</span>
        <SelectMenu
          ariaLabel="Number of dates"
          value={limit}
          onChange={onLimitChange}
          options={[
            { value: 10, label: "10 dates" },
            { value: 20, label: "20 dates" },
            { value: 50, label: "50 dates" },
            { value: 100, label: "100 dates" },
          ]}
        />
      </span>
      <span className="text-xs text-gray-500 dark:text-gray-400">
        {rows.length} {rows.length === 1 ? "date" : "dates"}
      </span>
    </div>
    {rows.length === 0 ? (
      <PanelEmpty message="No high-value dates surfaced yet." />
    ) : (
      <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
        <table className="w-full text-sm responsive-card-table">
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-3 py-2.5 text-left font-semibold">Date</th>
              <th className="px-3 py-2.5 text-left font-semibold">Window</th>
              <th className="px-3 py-2.5 text-right font-semibold">Days Out</th>
              <th className="px-3 py-2.5 text-right font-semibold">Your Rate</th>
              <th className="px-3 py-2.5 text-right font-semibold">Market Avg</th>
              <th className="px-3 py-2.5 text-right font-semibold">Above Baseline</th>
              <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Limited Avail.</th>
              <th className="px-3 py-2.5 text-right font-semibold">Comp / Your Occupancy</th>
              <th className="px-3 py-2.5 text-left font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const tint = ROW_TINT[row.action] || "hover:bg-gray-50 dark:hover:bg-gray-800/50";
              return (
                <tr key={`${row.check_in}-${index}`} className={`border-b dark:border-gray-800 transition ${tint}`}>
                  <td data-label="Date" className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtDate(row.check_in)}</td>
                  <td data-label="Window" className="px-3 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">{row.date_window_name || "—"}</td>
                  <td data-label="Days Out" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.days_out ?? "—"}</td>
                  <td data-label="Your Rate" className="px-3 py-2.5 text-right text-gray-900 dark:text-gray-100 font-semibold whitespace-nowrap">{fmtRate(row.your_rate)}</td>
                  <td data-label="Market Avg" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtRate(row.market_avg)}</td>
                  <td data-label="Above Baseline" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    <span>
                      +{fmtRate(row.above_baseline)} <span className="text-xs text-gray-500 dark:text-gray-400">({fmtPct(row.pct_above_baseline)})</span>
                    </span>
                  </td>
                  <td data-label="Limited Avail." className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.sold_out_competitors ?? 0}</td>
                  <td data-label="Comp / Your Occupancy" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    <span>
                      <InferredOccupancy value={row.comp_occupancy} /> / <InferredOccupancy value={row.your_occupancy} />
                    </span>
                  </td>
                  <td data-label="Action" className="px-3 py-2.5 whitespace-nowrap"><ActionBadge action={row.action} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ThinScroll>
    )}
  </div>
);

const HighValueDatesPanel = ({ clientId, say }) => {
  const [limit, setLimit] = useState(20);

  return (
    <AnalysisPanel
      id={`high-value-dates-${clientId}-${limit}`}
      title="High-Value Dates"
      tip="Top revenue-opportunity dates ranked by how many competitors are at limited availability and how far the market is priced above baseline. The ACTION column tells you whether to capture now, raise, monitor, or hold."
      description="Top dates by revenue opportunity, ranked by the action you should take."
      fetcher={() => getCiReportHighValueDates(clientId, { limit })}
      deps={[clientId, limit]}
    >
      {({ data }) => {
        const rows = Array.isArray(data) ? data : [];
        return (
          <>
            <PanelTakeaway panelKey="high_value_dates" say={say} />
            <HighValueDatesBody rows={rows} limit={limit} onLimitChange={setLimit} />
          </>
        );
      }}
    </AnalysisPanel>
  );
};

export default HighValueDatesPanel;
